
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, FileText, Shield, Scale, ChevronRight, 
  AlertTriangle, CheckCircle, Lock, Eye, 
  Mail, MapPin, Calendar, Info, ArrowLeft
} from 'lucide-react';

export default function CGUPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('cgu');

  // Détecte l'ancre dans l'URL et active le bon onglet
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'confidentialite' || hash === 'mentions') {
      setActiveTab(hash);
    }
  }, [location]);

  const tabs = [
    { id: 'cgu', label: 'CGU', icon: FileText },
    { id: 'confidentialite', label: 'Confidentialité', icon: Shield },
    { id: 'mentions', label: 'Mentions Légales', icon: Scale }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <span className="hidden sm:flex items-center space-x-2 text-white font-bold">
                <Scale className="text-orange-400" size={20} />
                <span>Informations Légales</span>
              </span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <Home size={20} className="relative z-10" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-12 pb-8 px-4">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-2xl opacity-60 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center backdrop-blur-xl border-4 border-white/20 shadow-2xl mx-auto transform hover:rotate-12 transition-transform">
              <Scale size={40} className="text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
            Informations Légales
          </h1>
          
          <p className="text-lg text-gray-300 mb-6">
            Conditions d'utilisation, confidentialité et mentions légales de MonMarket
          </p>

          <div className="inline-flex items-center space-x-2 bg-blue-500/20 px-4 py-2 rounded-xl border border-blue-500/30">
            <Calendar size={16} className="text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">Dernière mise à jour : Janvier 2025</span>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 relative z-10">
        
        {/* Tabs Navigation */}
        <div className="relative group mb-8">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-2 border border-white/20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center space-x-2 px-6 py-4 rounded-xl font-bold transition-all transform ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg scale-105'
                        : 'text-gray-300 hover:bg-white/10 hover:scale-105'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          
          {/* CGU Tab */}
          {activeTab === 'cgu' && (
            <div id="cgu" className="p-8 sm:p-12 animate-fadeIn">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl transform hover:rotate-12 transition-transform">
                  <FileText className="text-white" size={28} />
                </div>
                <h2 className="text-3xl font-black text-white">Conditions Générales d'Utilisation</h2>
              </div>

              <div className="space-y-8 text-gray-300">
                
                {/* Section 1 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-orange-400 text-2xl">1️⃣</span>
                    <span>Objet du service</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    MonMarket est une plateforme de mise en relation entre acheteurs et vendeurs en Côte d'Ivoire. Elle permet aux utilisateurs de publier, consulter et entrer en contact autour d'annonces de produits et services de tous genres (électronique, vêtements, accessoires, services, etc.).
                  </p>
                  <div className="bg-orange-500/20 border-l-4 border-orange-500 rounded-xl p-5 flex items-start space-x-3 hover:bg-orange-500/30 transition-all">
                    <Info className="text-orange-400 flex-shrink-0 mt-1" size={20} />
                    <p className="text-white">
                      <strong>Notre rôle :</strong> Le rôle de MonMarket est strictement limité à la mise en relation digitale entre utilisateurs, dans un cadre simple, transparent et accessible.
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-orange-400 text-2xl">2️⃣</span>
                    <span>Rôle et position de MonMarket</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    Il est important de bien comprendre la position de MonMarket dans l'écosystème du commerce en ligne :
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      "MonMarket n'est ni vendeur ni acheteur",
                      "MonMarket ne possède aucun produit",
                      "MonMarket ne participe pas aux transactions",
                      "MonMarket ne garantit pas la conclusion d'une vente"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all transform hover:scale-105">
                        <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-orange-400 text-2xl">3️⃣</span>
                    <span>Limites de responsabilité</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    MonMarket ne saurait être tenu responsable en cas de :
                  </p>
                  <div className="space-y-3">
                    {[
                      "Escroqueries, fraudes ou fausses déclarations d'un utilisateur",
                      "Qualité, authenticité ou conformité réelle des produits",
                      "Litiges, désaccords ou conflits entre acheteurs et vendeurs",
                      "Paiements effectués en dehors de la plateforme",
                      "Informations incomplètes ou non communiquées par le vendeur"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-red-500/10 rounded-xl p-4 border border-red-500/30 hover:border-red-500/50 hover:bg-red-500/20 transition-all">
                        <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-yellow-500/20 border-l-4 border-yellow-500 rounded-xl p-5 mt-4 hover:bg-yellow-500/30 transition-all">
                    <p className="text-yellow-200 font-bold mb-2">🚨 Prudence obligatoire</p>
                    <p className="text-sm">
                      MonMarket recommande fortement à chaque utilisateur de vérifier les informations avant toute transaction, privilégier les rencontres sécurisées, tester les produits et ne jamais envoyer d'argent sans garanties suffisantes.
                    </p>
                  </div>
                </section>

                {/* Section 4 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-orange-400 text-2xl">4️⃣</span>
                    <span>Engagements des utilisateurs</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    En utilisant MonMarket, chaque utilisateur s'engage à :
                  </p>
                  <div className="space-y-3">
                    {[
                      "Fournir des informations exactes, honnêtes et vérifiables",
                      "Publier uniquement des annonces légales et non frauduleuses",
                      "Respecter les autres utilisateurs",
                      "Agir avec prudence lors des échanges",
                      "Ne pas tenter de contourner les règles de sécurité"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-green-500/10 rounded-xl p-4 border border-green-500/30 hover:border-green-500/50 hover:bg-green-500/20 transition-all">
                        <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 5 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-orange-400 text-2xl">5️⃣</span>
                    <span>Données personnelles</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    MonMarket collecte uniquement les données nécessaires au bon fonctionnement du service : création de compte, publication d'annonces, prise de contact entre utilisateurs.
                  </p>
                  <div className="bg-blue-500/20 border-l-4 border-blue-500 rounded-xl p-5 hover:bg-blue-500/30 transition-all">
                    <p className="text-blue-200 font-bold mb-2">🔒 Confidentialité garantie</p>
                    <p className="text-sm">
                      Les données personnelles ne sont jamais revendues, ne sont pas partagées avec des tiers non autorisés et sont traitées de manière confidentielle et sécurisée.
                    </p>
                  </div>
                </section>

                {/* Acceptation */}
                <div className="relative group/accept">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-20 group-hover/accept:opacity-40 transition duration-500"></div>
                  <div className="relative bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-2xl p-8 border border-orange-500/30 text-center transform hover:scale-105 transition-all">
                    <CheckCircle className="text-orange-400 mx-auto mb-4" size={40} />
                    <p className="text-white font-bold text-lg mb-2">Acceptation des conditions</p>
                    <p className="text-sm">
                      L'utilisation de la plateforme MonMarket implique l'acceptation pleine et entière des présentes Conditions Générales d'Utilisation.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Confidentialité Tab */}
          {activeTab === 'confidentialite' && (
            <div id="Confidentialite" className="p-8 sm:p-12 animate-fadeIn">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl transform hover:rotate-12 transition-transform">
                  <Shield className="text-white" size={28} />
                </div>
                <h2 className="text-3xl font-black text-white"  > Politique de Confidentialité</h2>
              </div>

              <div className="space-y-8 text-gray-300">
                
                <div className="bg-blue-500/20 border-l-4 border-blue-500 rounded-xl p-5 mb-6 hover:bg-blue-500/30 transition-all">
                  <p className="text-blue-200 font-bold mb-2">⚖️ Cadre légal</p>
                  <p className="text-sm">
                    Conforme à la loi n°2013-450 du 19 juin 2013 relative à la protection des données à caractère personnel en Côte d'Ivoire et aux recommandations de l'ARTCI.
                  </p>
                </div>

                {/* Section 1 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">1️⃣</span>
                    <span>Responsable du traitement</span>
                  </h3>
                  <div className="bg-white/5 rounded-xl p-6 space-y-3 hover:bg-white/10 transition-all border border-white/10">
                    <p><strong className="text-white">Nom :</strong> MonMarket</p>
                    <p><strong className="text-white">Activité :</strong> Plateforme de mise en relation acheteurs et vendeurs</p>
                    <div className="flex items-center space-x-2">
                      <MapPin size={16} className="text-orange-400" />
                      <span>Côte d'Ivoire</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail size={16} className="text-orange-400" />
                      <span>contact@monmarket.ci</span>
                    </div>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">2️⃣</span>
                    <span>Données collectées</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    Dans le cadre de l'utilisation de MonMarket, les données suivantes peuvent être collectées :
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      "Identité (nom, prénom ou pseudonyme)",
                      "Coordonnées (email, téléphone)",
                      "Informations relatives aux annonces",
                      "Échanges via la plateforme",
                      "Données de connexion (IP, logs)",
                      "Cookies de fonctionnement"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all transform hover:scale-105">
                        <Eye className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-5 mt-4 flex items-start space-x-3 hover:bg-red-500/30 transition-all">
                    <Lock className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-sm">
                      <strong className="text-white">Important :</strong> MonMarket ne collecte ni ne traite aucune donnée bancaire ou sensible.
                    </p>
                  </div>
                </section>

                {/* Section 3 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">3️⃣</span>
                    <span>Finalités du traitement</span>
                  </h3>
                  <div className="space-y-3">
                    {[
                      "Création et gestion des comptes utilisateurs",
                      "Publication et gestion des annonces",
                      "Mise en relation entre acheteurs et vendeurs",
                      "Prévention des fraudes et usages abusifs",
                      "Amélioration continue du service",
                      "Respect des obligations légales"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all">
                        <ChevronRight className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 4 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">4️⃣</span>
                    <span>Sécurité et confidentialité</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    MonMarket met en œuvre toutes les mesures techniques et organisationnelles raisonnables pour garantir la sécurité, la confidentialité et la protection contre l'accès non autorisé.
                  </p>
                  <div className="relative group/security">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur opacity-20 group-hover/security:opacity-40 transition duration-500"></div>
                    <div className="relative bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl p-8 border border-green-500/30 transform hover:scale-105 transition-all">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                        {[
                          { icon: Lock, text: "Jamais vendues" },
                          { icon: Shield, text: "Jamais louées" },
                          { icon: CheckCircle, text: "Protégées" }
                        ].map((item, idx) => (
                          <div key={idx} className="transform hover:scale-110 transition-transform">
                            <item.icon className="text-green-400 mx-auto mb-3" size={32} />
                            <p className="text-sm font-bold text-white">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 5 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">5️⃣</span>
                    <span>Durée de conservation</span>
                  </h3>
                  <p className="leading-relaxed bg-white/5 rounded-xl p-5 hover:bg-white/10 transition-all">
                    Les données sont conservées pendant la durée d'activité du compte utilisateur ou pour la durée nécessaire au respect des obligations légales. À la suppression du compte, les données sont supprimées ou anonymisées.
                  </p>
                </section>

                {/* Section 6 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-blue-400 text-2xl">6️⃣</span>
                    <span>Vos droits</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    Conformément à la loi ivoirienne, chaque utilisateur dispose des droits suivants :
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      "Droit d'accès",
                      "Droit de rectification",
                      "Droit d'opposition",
                      "Droit à l'effacement",
                      "Droit à la limitation",
                      "Droit à la portabilité"
                    ].map((item, idx) => (
                      <div key={idx} className="bg-blue-500/10 rounded-xl p-4 text-center border border-blue-500/30 hover:border-blue-500/50 hover:bg-blue-500/20 transition-all transform hover:scale-105">
                        <CheckCircle className="text-blue-400 mx-auto mb-2" size={20} />
                        <p className="text-xs font-medium">{item}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-orange-500/20 border-l-4 border-orange-500 rounded-xl p-5 mt-4 flex items-start space-x-3 hover:bg-orange-500/30 transition-all">
                    <Mail className="text-orange-400 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-sm">
                      Pour exercer vos droits : <strong className="text-white">contact@monmarket.ci</strong>
                    </p>
                  </div>
                </section>

              </div>
            </div>
          )}

          {/* Mentions Légales Tab */}
          {activeTab === 'mentions' && (
            <div id="Mentions" className="p-8 sm:p-12 animate-fadeIn">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl transform hover:rotate-12 transition-transform">
                  <Scale className="text-white" size={28} />
                </div>
                <h2 className="text-3xl font-black text-white" >Mentions Légales</h2>
              </div>

              <div className="space-y-8 text-gray-300">
                
                {/* Section 1 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-purple-400 text-2xl">1️⃣</span>
                    <span>Éditeur du site</span>
                  </h3>
                  <div className="bg-white/5 rounded-xl p-6 space-y-4 hover:bg-white/10 transition-all border border-white/10">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Nom du site</p>
                      <p className="text-white font-bold text-lg">MonMarket</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Nature de l'activité</p>
                      <p className="text-white">Plateforme de mise en relation acheteurs et vendeurs</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Zone d'activité</p>
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} className="text-orange-400" />
                        <span className="text-white">République de Côte d'Ivoire</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Contact</p>
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-orange-400" />
                        <span className="text-white">contact@monmarket.ci</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-purple-400 text-2xl">2️⃣</span>
                    <span>Statut de la plateforme</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    MonMarket est un prestataire de services numériques agissant exclusivement comme intermédiaire technique de mise en relation.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      "MonMarket n'est ni vendeur ni acheteur",
                      "MonMarket ne détient aucun produit",
                      "MonMarket ne participe pas aux transactions",
                      "MonMarket ne garantit pas la conclusion des ventes"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-2 bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all transform hover:scale-105">
                        <Info className="text-purple-400 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-purple-400 text-2xl">3️⃣</span>
                    <span>Responsabilité éditoriale</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    Les contenus publiés (annonces, descriptions, images, prix) sont placés sous la responsabilité exclusive des utilisateurs.
                  </p>
                  <p className="leading-relaxed bg-white/5 rounded-xl p-5 hover:bg-white/10 transition-all">
                    MonMarket se réserve le droit de supprimer tout contenu illicite ou frauduleux et de suspendre ou supprimer un compte en cas de non-respect des CGU.
                  </p>
                </section>

                {/* Section 4 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-purple-400 text-2xl">4️⃣</span>
                    <span>Propriété intellectuelle</span>
                  </h3>
                  <div className="bg-yellow-500/20 border-l-4 border-yellow-500 rounded-xl p-5 hover:bg-yellow-500/30 transition-all">
                    <p className="text-sm leading-relaxed">
                      Tous les éléments du site MonMarket (textes, logo, design, code, base de données) sont protégés par les lois ivoiriennes et internationales relatives à la propriété intellectuelle. <strong className="text-white">Toute reproduction sans autorisation est strictement interdite.</strong>
                    </p>
                  </div>
                </section>

                {/* Section 5 */}
                <section className="transform hover:scale-[1.01] transition-transform">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <span className="text-purple-400 text-2xl">5️⃣</span>
                    <span>Droit applicable et juridiction</span>
                  </h3>
                  <p className="mb-4 leading-relaxed">
                    Les présentes Mentions Légales sont régies par le droit ivoirien.
                  </p>
                  <div className="bg-white/5 rounded-xl p-5 hover:bg-white/10 transition-all">
                    <p className="text-sm mb-3 font-bold text-white">En cas de litige :</p>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start space-x-2">
                        <ChevronRight className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                        <span>Une solution amiable sera privilégiée</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <ChevronRight className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                        <span>À défaut, les juridictions compétentes de Côte d'Ivoire seront seules habilitées</span>
                      </li>
                    </ul>
                  </div>
                </section>

              </div>
            </div>
          )}

        </div>
        </div>

        {/* Contact Footer */}
        <div className="mt-8 relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center hover:bg-white/10 transition-all">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center justify-center space-x-2">
              <Mail size={24} className="text-orange-400" />
              <span>Une question ?</span>
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex items-center space-x-3 text-gray-300 bg-white/5 px-6 py-3 rounded-xl hover:bg-white/10 transition-all transform hover:scale-105">
                <Mail size={20} className="text-orange-400" />
                <span className="font-medium">contact@monmarket.ci</span>
              </div>
              <div className="hidden sm:block text-gray-600">•</div>
              <div className="flex items-center space-x-3 text-gray-300 bg-white/5 px-6 py-3 rounded-xl hover:bg-white/10 transition-all transform hover:scale-105">
                <MapPin size={20} className="text-orange-400" />
                <span className="font-medium">Côte d'Ivoire</span>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}